import React, { useState, useEffect, useRef } from 'react';
import { 
  Server, 
  Play, 
  Square, 
  Trash2, 
  Share2, 
  AlertTriangle, 
  CheckCircle, 
  Loader,
  ExternalLink,
  Users,
  HardDrive,
  Cpu,
  Link,
  Layers2
} from 'lucide-react';
import { createBlenderVirtualDoc } from '../../services/docServices';
import styles from './ManageServersModal.module.css';

// Optional ViewerContext usage
let useViewerContext;
try {
  const viewerContext = require('../../context/ViewerContext');
  useViewerContext = viewerContext.useViewerContext;
} catch (error) {
  // Fallback when ViewerContext is not available
  useViewerContext = () => ({ openFile: () => {} });
}

function BlenderServerManager({ tenantId, onError, kbId, onServerConnected, isOpen = true, isActiveTab = true }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedServer, setSelectedServer] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [pollingServers, setPollingServers] = useState(new Set());
  const [pollingInterval, setPollingInterval] = useState(null);
  // Add a local state to track user-initiated server actions
  const [localServerIntents, setLocalServerIntents] = useState({});
  
  // Safely get openFile from context
  let openFile;
  try {
    const viewerContext = useViewerContext();
    openFile = viewerContext.openFile;
  } catch (error) {
    // Fallback when context is not available
    openFile = () => {};
  }

  // Per-server polling intervals and timers
  const [serverPollIntervals, setServerPollIntervals] = useState({}); // { serverName: intervalMs }
  const pollTimers = useRef({}); // { serverName: timerId }

  // Helper to clear all polling timers
  const clearAllPollTimers = () => {
    Object.values(pollTimers.current).forEach(clearTimeout);
    pollTimers.current = {};
  };

  // Polling logic with exponential backoff
  const pollServerStatusWithBackoff = (serverName, intervalMs = 2000) => {
    if (pollTimers.current[serverName]) clearTimeout(pollTimers.current[serverName]);
    pollTimers.current[serverName] = setTimeout(async () => {
      try {
        const response = await fetch(`/v1/eks_blender/status/${serverName}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.RetCode === 0) {
          const statusData = data.data;
          setServers(prevServers =>
            prevServers.map(server =>
              server.server_name === serverName
                ? { ...server, ...statusData }
                : server
            )
          );
          // Exponential backoff logic
          let nextInterval = intervalMs;
          if (statusData.status === 'running') {
            nextInterval = Math.min(intervalMs * 2, 30000); // max 30s
          } else {
            nextInterval = 2000; // reset to 2s for transition states
          }
          setServerPollIntervals(prev => ({ ...prev, [serverName]: nextInterval }));
          if (isOpen && isActiveTab && (statusData.status === 'creating' || statusData.status === 'starting' || statusData.status === 'running')) {
            pollServerStatusWithBackoff(serverName, nextInterval);
          } else {
            if (pollTimers.current[serverName]) clearTimeout(pollTimers.current[serverName]);
            delete pollTimers.current[serverName];
          }
        }
      } catch (err) {
        setServerPollIntervals(prev => ({ ...prev, [serverName]: 2000 }));
        if (isOpen && isActiveTab) {
          pollServerStatusWithBackoff(serverName, 2000);
        }
      }
    }, intervalMs);
  };

  useEffect(() => {
    if (!isOpen || !isActiveTab) {
      clearAllPollTimers();
      return;
    }
    servers.forEach(server => {
      if (['creating', 'starting', 'running'].includes(server.status)) {
        if (!pollTimers.current[server.server_name]) {
          const interval = serverPollIntervals[server.server_name] || 2000;
          pollServerStatusWithBackoff(server.server_name, interval);
        }
      } else {
        if (pollTimers.current[server.server_name]) {
          clearTimeout(pollTimers.current[server.server_name]);
          delete pollTimers.current[server.server_name];
        }
      }
    });
    return clearAllPollTimers;
    // eslint-disable-next-line
  }, [isOpen, isActiveTab, servers]);

  useEffect(() => clearAllPollTimers, []);

  // Fetch servers on component mount
  useEffect(() => {
    fetchServers();
  }, []); // Only run on mount

  const fetchServers = async (showLoading = true) => {
    if (showLoading && !loading) {
      setLoading(true);
    }
    setError('');
    
    try {
      const response = await fetch('/v1/eks_blender/list', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        const newServers = data.data.servers || [];
        setServers(newServers);
        // Clear local intent for servers that are no longer in transition
        setLocalServerIntents((prev) => {
          const updated = { ...prev };
          newServers.forEach(server => {
            if (server.status !== 'creating' && server.status !== 'starting') {
              delete updated[server.server_name];
            }
          });
          return updated;
        });

      } else {
        setError(data.retmsg || 'Failed to fetch Blender servers');
      }
    } catch (err) {
      console.error('Error fetching Blender servers:', err);
      setError('Failed to fetch Blender servers. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };



  const createServer = async () => {
    setActionLoading({ ...actionLoading, create: true });
    setError('');
    const newServerName = `blender-${Date.now()}`;
    
    // Immediately add the server to local state with creating status
    const newServer = {
      server_name: newServerName,
      status: 'creating',
      pod_message: 'Initializing server...',
      created_at: new Date().toISOString()
    };
    setServers(prev => [newServer, ...prev]);
    
    // Set local intent for this server
    setLocalServerIntents((prev) => ({ ...prev, [newServerName]: 'creating' }));
    
    try {
      const response = await fetch('/v1/eks_blender/create', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          server_name: newServerName
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        // Start polling for this server immediately
        pollServerStatusWithBackoff(newServerName, 2000);
        // Fetch servers to get the actual server data from backend (don't show loading)
        await fetchServers(false);
      } else {
        // Remove the server from local state if creation failed
        setServers(prev => prev.filter(s => s.server_name !== newServerName));
        setLocalServerIntents(prev => {
          const updated = { ...prev };
          delete updated[newServerName];
          return updated;
        });
        setError(data.retmsg || 'Failed to create Blender server');
      }
    } catch (err) {
      // Remove the server from local state if creation failed
      setServers(prev => prev.filter(s => s.server_name !== newServerName));
      setLocalServerIntents(prev => {
        const updated = { ...prev };
        delete updated[newServerName];
        return updated;
      });
      console.error('Error creating Blender server:', err);
      setError('Failed to create Blender server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, create: false });
    }
  };

  const startServer = async (serverName) => {
    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    // Immediately update the server status to starting
    setServers(prev => prev.map(server =>
      server.server_name === serverName
        ? { ...server, status: 'starting', pod_message: 'Starting Blender...' }
        : server
    ));
    
    // Set local intent for this server
    setLocalServerIntents((prev) => ({ ...prev, [serverName]: 'starting' }));
    
    try {
      const response = await fetch(`/v1/eks_blender/start/${serverName}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        // Start polling for this server immediately
        pollServerStatusWithBackoff(serverName, 2000);
        // Fetch servers to get the actual server data from backend (don't show loading)
        await fetchServers(false);
      } else {
        // Revert the server status if start failed
        setServers(prev => prev.map(server =>
          server.server_name === serverName
            ? { ...server, status: 'stopped', pod_message: '' }
            : server
        ));
        setLocalServerIntents(prev => {
          const updated = { ...prev };
          delete updated[serverName];
          return updated;
        });
        setError(data.retmsg || 'Failed to start Blender server');
      }
    } catch (err) {
      // Revert the server status if start failed
      setServers(prev => prev.map(server =>
        server.server_name === serverName
          ? { ...server, status: 'stopped', pod_message: '' }
          : server
      ));
      setLocalServerIntents(prev => {
        const updated = { ...prev };
        delete updated[serverName];
        return updated;
      });
      console.error('Error starting Blender server:', err);
      setError('Failed to start Blender server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const stopServer = async (serverName) => {
    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_blender/stop/${serverName}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(false); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to stop Blender server');
      }
    } catch (err) {
      console.error('Error stopping Blender server:', err);
      setError('Failed to stop Blender server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const deleteServer = async (serverName) => {
    if (!confirm(`Are you sure you want to delete Blender server "${serverName}"? This will permanently delete the server but preserve your storage.`)) {
      return;
    }

    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_blender/delete/${serverName}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(false); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to delete Blender server');
      }
    } catch (err) {
      console.error('Error deleting Blender server:', err);
      setError('Failed to delete Blender server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const shareServer = async () => {
    if (!shareUserId.trim()) {
      setError('Please enter a user ID to share with');
      return;
    }

    setActionLoading({ ...actionLoading, share: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_blender/share/${selectedServer}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_user_id: shareUserId.trim()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        setShowShareModal(false);
        setShareTenantId('');
        setSelectedServer(null);
        await fetchServers(false); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to share Blender server');
      }
    } catch (err) {
      console.error('Error sharing Blender server:', err);
      setError('Failed to share Blender server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, share: false });
    }
  };

  const openBlender = async (serverName) => {
    const server = servers.find(s => s.server_name === serverName);
    if (server && server.status === 'running') {
      try {
        const response = await fetch(`/v1/eks_blender/url/${serverName}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data.RetCode === 0) {
          openFile({
            id: `blender-${serverName}`,
            name: `Blender: ${serverName}.blender`,
            type: 'blender',
            url: data.data.url,
          });
        } else {
          setError(data.retmsg || 'Failed to get Blender URL');
        }
      } catch (err) {
        console.error('Error getting Blender URL:', err);
        setError('Failed to get Blender URL. Please try again.');
      }
    }
  };

  const connectToKnowledgebase = async (serverName) => {
    if (!kbId) {
      setError('No knowledgebase selected');
      onError?.('No knowledgebase selected');
      return;
    }

    setActionLoading((prev) => ({ ...prev, [`connect-${serverName}`]: true }));
    setError('');
    
    try {
      const result = await createBlenderVirtualDoc(serverName, kbId);
      setError('');
      onServerConnected?.(result);
    } catch (err) {
      const errorMessage = err.message || 'Failed to connect server to knowledgebase';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setActionLoading((prev) => ({ ...prev, [`connect-${serverName}`]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'text-green-500';
      case 'stopped':
        return 'text-yellow-500';
      case 'creating':
        return 'text-blue-500';
      case 'starting':
        return 'text-blue-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'stopped':
        return <Square className="h-4 w-4 text-yellow-500" />;
      case 'creating':
        return <Loader className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'starting':
        return <Loader className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Server className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className={styles.serverSectionHeader}>
        <h3 className={styles.serverSectionTitle}>Blender Servers</h3>
        <button
          onClick={createServer}
          disabled={actionLoading.create}
          className={styles.createServerButton}
        >
          {actionLoading.create ? (
            <>
              <Loader className={styles.createServerButtonIcon} />
              Creating...
            </>
          ) : (
            <>
              <Layers2 className={styles.createServerButtonIcon} />
              Create Server
            </>
          )}
        </button>
      </div>
      {/* Servers List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading servers...</span>
          </div>
        ) : servers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Layers2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No Blender servers found.</p>
            <p className="text-sm">Create your first server to get started.</p>
          </div>
        ) : (
          servers.map((server) => {
            let displayStatus = server.status;
            if ((server.status === 'creating' || server.status === 'starting') && localServerIntents[server.server_name]) {
              displayStatus = localServerIntents[server.server_name];
            }
            return (
              <div
                key={server.server_name}
                className={styles.serverCard}
              >
                <div className={styles.serverInfo}>
                  {getStatusIcon(displayStatus)}
                  <div>
                    <h4 className={styles.serverName}>{server.server_name}</h4>
                    <p className={styles.serverStatus}>
                      {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                      {server.pod_message && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          - {server.pod_message}
                        </span>
                      )}
                    </p>
                    {server.created_at && (
                      <p className={styles.serverDate}>
                        Created: {new Date(server.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.serverActions}>
                  {kbId && (
                    <button
                      onClick={() => connectToKnowledgebase(server.server_name)}
                      disabled={actionLoading[`connect-${server.server_name}`]}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Connect to Knowledgebase"
                    >
                      {actionLoading[`connect-${server.server_name}`] ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Link className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedServer(server.server_name);
                      setShowShareModal(true);
                    }}
                    disabled={server.status === 'creating' || server.status === 'starting'}
                    className={`${styles.serverActionButton} ${styles.share}`}
                    title="Share Server"
                  >
                    {(server.status === 'creating' || server.status === 'starting') ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Share2 className="h-4 w-4" />
                    )}
                  </button>
                  {displayStatus === 'stopped' && (
                    <button
                      onClick={() => startServer(server.server_name)}
                      disabled={actionLoading[server.server_name]}
                      className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Start Server"
                    >
                      {actionLoading[server.server_name] ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {displayStatus === 'running' && (
                    <button
                      onClick={() => stopServer(server.server_name)}
                      disabled={actionLoading[server.server_name]}
                      className={`${styles.serverActionButton} ${styles.stop}`}
                      title="Stop Server"
                    >
                      {actionLoading[server.server_name] ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => deleteServer(server.server_name)}
                    disabled={actionLoading[server.server_name] || server.status === 'creating' || server.status === 'starting'}
                    className={`${styles.serverActionButton} ${styles.delete}`}
                    title="Delete Server"
                  >
                    {actionLoading[server.server_name] || server.status === 'creating' || server.status === 'starting' ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-card shadow-xl border border-border">
            <div className="mb-4">
              <div className="flex items-center mb-3">
                <div className="size-8 rounded-full bg-purple-500/10 text-purple-500 flex items-center justify-center mr-3">
                  <Users className="h-4 w-4" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Share Blender Server</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Share server "{selectedServer}" with another user. They will get their own isolated instance with access to the same data.
              </p>
            </div>
            <div className="mb-4">
              <label htmlFor="shareUserId" className="block text-sm font-medium text-foreground mb-2">
                Target User ID
              </label>
              <input
                id="shareUserId"
                type="text"
                value={shareUserId}
                onChange={(e) => setShareUserId(e.target.value)}
                placeholder="Enter user ID to share with"
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setShareTenantId('');
                  setSelectedServer(null);
                }}
                className="px-4 py-2 rounded-md border border-border bg-background hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={shareServer}
                disabled={actionLoading.share || !shareUserId.trim()}
                className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center disabled:opacity-50"
              >
                {actionLoading.share ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Sharing...
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Server
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlenderServerManager; 